<template>
  <a
    v-if="!isRouteLink"
    :class="classList"
    :href="link"
    :tabindex="tabindex"
    :target="isExternal ? '_blank' : '_self'"
    @mouseenter="isHoveredOn = true"
    @mouseleave="isHoveredOn = false"
  >
    <template
      v-for="(child, index) in $slots.default ? $slots.default() : []"
      :key="index"
    >
      <template v-if="child?.type?.__name === 'Icon'">
        <component
          :is="child"
          :icon-props="{ color: iconColor, secondaryColor: iconColor }"
        />
      </template>
      <template v-else>
        <span>
          <component :is="child" />
        </span>
      </template>
    </template>
    <div v-if="isExternal || hasArrow" class="st-link__icon-container">
      <StIcon
        v-if="isExternal"
        name="new-tab"
        alt="Öppnas i ny flik"
        :aria-hidden="false"
      />
      <div v-if="hasArrow" class="st-link__icon-container--rotated">
        <StIcon name="chevron" />
      </div>
    </div>
  </a>
  <router-link
    v-else
    :class="classList"
    :to="link"
    :tabindex="tabindex"
    role="link"
    @mouseenter="isHoveredOn = true"
    @mouseleave="isHoveredOn = false"
  >
    <template
      v-for="(child, index) in $slots.default ? $slots.default() : []"
      :key="index"
    >
      <template v-if="child?.type?.__name === 'Icon'">
        <component :is="child" :icon-props="{ color: iconColor }" />
      </template>
      <template v-else>
        <span>
          <component :is="child" />
        </span>
      </template>
    </template>
    <div v-if="hasArrow" class="st-link__icon-container">
      <div class="st-link__icon-container--rotated">
        <st-icon name="chevron" />
      </div>
    </div>
  </router-link>
</template>

<script setup>
import { ref, computed } from "vue";
import { StIcon } from "@/index";
import useIconColor from "@/composables/useIconColor.js";

const props = defineProps({
  isExternal: { type: Boolean },
  hasArrow: { type: Boolean },
  isRouteLink: { type: Boolean },
  link: { type: String, required: true },
  tabindex: { type: String, default: "0" },
  buttonPrimary: { type: Boolean },
  buttonSecondary: { type: Boolean },
  buttonTertiary: { type: Boolean },
  buttonQuaternary: { type: Boolean },
  buttonRectangular: { type: Boolean },
  buttonSmall: { type: Boolean },
  buttonFullWidth: { type: Boolean },
});

const buttonType = props.buttonPrimary
  ? "primary"
  : props.buttonSecondary
  ? "secondary"
  : props.buttonTertiary
  ? "tertiary"
  : props.buttonQuaternary
  ? "quaternary"
  : null;

const classList = computed(() => {
  const link = "st-link";
  return [
    link,
    buttonType && `${link}-button--${buttonType}`,
    props.buttonSmall && `${link}-button--small`,
    props.buttonRectangular && `${link}-button--rectangular`,
    props.buttonFullWidth && `${link}-button--full-width`,
  ];
});

const isHoveredOn = ref(false);

const { iconColor } = useIconColor(buttonType, isHoveredOn);
</script>

<style lang="scss" scoped>
@import "../../scss/main.scss";
@import "../button/button.scss";

.st-link {
  display: inline-flex;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s ease;
  font-family: var(--font-primary) !important;
  padding: 0;
  font-size: rem-calc(16);
  font-weight: var(--font-weight-regular);
  font-style: normal;
  line-height: rem-calc(24);
  color: var(--text-color-interactive);

  &__icon-container {
    display: inline-flex;
    align-items: center;
    margin-left: rem-calc(4);

    &--rotated {
      display: inline-flex;
      transform: rotate(-90deg);
    }
  }

  &:focus {
    outline: rem-calc(1) solid var(--border-color-quaternary);
    outline-offset: rem-calc(4);
  }

  &:hover {
    color: var(--text-color-primary);
  }

  &-button {
    @include button-modifiers;
  }
}
</style>
